import { type ComponentProps } from 'react';
import { styled, css } from 'styled-components';
import { type ColorName } from '../../styles/colors.ts';
import { resetButtonStyles } from '../../styles/helpers.ts';

export type IconButtonProps = {
  href?: string;
  target?: '_blank' | '_self';
  rel?: 'noopener noreferrer';
  size?: Size;
  color?: ColorName;
  bgColor?: ColorName;
  noBorder?: boolean;
} & Omit<ComponentProps<typeof Button>, keyof StyledButtonProps>;

const IconButton = ({
  children,
  href,
  size = 'normal',
  color = 'accentSecondary',
  bgColor,
  noBorder = false,
  ...props
}: IconButtonProps) => {
  const ButtonComponent = href ? 'a' : 'button';

  return (
    <Button
      as={ButtonComponent}
      href={href}
      $size={size}
      $color={color}
      $bgColor={bgColor}
      $noBorder={noBorder}
      {...props}
    >
      {children}
    </Button>
  );
};

type Size = 'small' | 'normal';

const stylesBySize: {
  [key in Size]: ReturnType<typeof css>;
} = {
  small: css`
    width: 32px;
    height: 32px;

    ${({ theme }) => theme.getUpMedia('md')} {
      width: ${({ theme }) => theme.responsive(32)};
      height: ${({ theme }) => theme.responsive(32)};
    }
  `,
  normal: css`
    width: 42px;
    height: 42px;

    ${({ theme }) => theme.getUpMedia('md')} {
      width: ${({ theme }) => theme.responsive(42)};
      height: ${({ theme }) => theme.responsive(42)};
    }
  `,
};

type StyledButtonProps = {
  $size: Size;
  $color: ColorName;
  $bgColor?: ColorName;
  $noBorder?: boolean;
};

const Button = styled.button<StyledButtonProps>`
  ${resetButtonStyles};
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ $size }) => stylesBySize[$size]}
  border: ${({ $noBorder, theme, $color }) =>
    $noBorder ? 'none' : `2px solid ${theme.getColor($color)}`};
  border-radius: 50%;
  color: ${({ theme, $color }) => theme.getColor($color)};
  background: ${({ theme, $bgColor }) => ($bgColor ? theme.getColor($bgColor) : 'transparent')};

  > svg {
    max-width: 100%;
    max-height: 100%;
    color: inherit;
  }
`;

export { IconButton };
